/**
 * Forest and avalanches, from crown cover (0–1 per terrain cell, NIBIO SR16):
 *
 * - Dense forest keeps snow from releasing: no release where crown cover ≥ `releaseMaxCover`
 *   (stems anchor the snowpack; the usual threshold for protective forest is ~50 % cover).
 * - Flowing snow is held back by the trees — detrainment, Feistl et al. (2014): mass leaves the
 *   flow at K/|u| per unit area and time, K ≈ 10 Pa in open or old forest and ≈ 30 Pa in dense
 *   stands. Their tests found this reproduces small and medium avalanches in forest better than
 *   raising the friction (μ + 0.02, ξ = 400), which overestimated runout. Friction is unchanged.
 */
export const FOREST_MODEL = {
  /** Below this crown cover, no forest effect. */
  minCover: 0.1,
  /** K (Pa) at open-forest cover, rising linearly to the dense value. */
  openCover: 0.3,
  openK: 10,
  denseCover: 0.8,
  denseK: 30,
  releaseMaxCover: 0.5,
} as const;

/** Detrainment coefficient K (Pa) for a crown cover (0–1). */
export function detrainmentK(cover: number): number {
  const f = FOREST_MODEL;
  if (!(cover >= f.minCover)) return 0;
  if (cover <= f.openCover) return (f.openK * cover) / f.openCover;
  const t = Math.min(1, (cover - f.openCover) / (f.denseCover - f.openCover));
  return f.openK + t * (f.denseK - f.openK);
}

export function detrainmentField(cover: Float32Array): Float32Array {
  const out = new Float32Array(cover.length);
  for (let i = 0; i < cover.length; i++) out[i] = detrainmentK(cover[i]!);
  return out;
}
